import { applyDeadzone } from '../engine/vector'

/**
 * A single normalized input contract. Any source — keyboard, joystick, tap-to-walk,
 * future gamepad — implements this. The player controller (Plan 05) reads only
 * `InputSystem.vector` and `InputSystem.consumeReset()`; it NEVER branches on
 * the underlying source type (MOVE-04).
 */
export interface InputSource {
  /** Returns the source's current desired direction, magnitude ≤ 1. */
  read(): { x: number; y: number }
  /** Optional: true when the source has just requested a world reset (e.g. R key). */
  readReset?(): boolean
}

/** 15% deadzone per CONTEXT.md — keeps stick drift and tiny touch jitter from moving the player. */
const DEADZONE = 0.15

/**
 * Aggregates N input sources into one normalized `{x,y}` vector per tick.
 * Strategy: pick the source with the highest instantaneous magnitude (loudest wins),
 * then run the result through a 15% deadzone. This means a player can have keyboard
 * and joystick wired simultaneously without conflict — whichever is actively being
 * driven wins, without additive drift.
 */
export class InputSystem {
  private sources: InputSource[] = []
  vector: { x: number; y: number } = { x: 0, y: 0 }
  resetRequested = false

  register(source: InputSource): void {
    this.sources.push(source)
  }

  tick(): void {
    let best = { x: 0, y: 0 }
    let bestMagSq = 0
    for (const s of this.sources) {
      const v = s.read()
      const magSq = v.x * v.x + v.y * v.y
      if (magSq > bestMagSq) {
        bestMagSq = magSq
        best = v
      }
      if (s.readReset?.()) {
        this.resetRequested = true
      }
    }
    this.vector = applyDeadzone(best, DEADZONE)
  }

  /** Returns current resetRequested state and clears it. Intended for one-shot handling. */
  consumeReset(): boolean {
    const was = this.resetRequested
    this.resetRequested = false
    return was
  }
}
